import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { CalendarCheck, Clock, CheckCircle2, Loader2, Repeat, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

const SPORTS = ["Calcio", "Basket", "Pallavolo", "Tennis", "Nuoto", "Atletica", "Rugby", "Altro"];
const WEEKS_OPTIONS = ["2", "4", "6", "8", "12"];

const todayPlus = (days) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().split("T")[0];
};

const fmtDate = (iso) => iso.split("-").reverse().join("/");

export const Booking = () => {
  const [date, setDate] = useState(todayPlus(7));
  const [slots, setSlots] = useState([]);
  const [taken, setTaken] = useState([]);
  const [closed, setClosed] = useState(false);
  const [slot, setSlot] = useState("");
  const [form, setForm] = useState({ name: "", email: "", phone: "", sport: "", notes: "" });
  const [recurring, setRecurring] = useState(false);
  const [weeks, setWeeks] = useState("4");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    setSlot("");
    api.get("/bookings/slots", { params: { date } })
      .then((r) => { setSlots(r.data.slots); setTaken(r.data.taken); setClosed(!!r.data.closed); })
      .catch(() => toast.error("Impossibile caricare le disponibilità"));
  }, [date]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!slot) { toast.error("Seleziona una fascia oraria"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/bookings", {
        ...form, date, time_slot: slot,
        recurrence_weeks: recurring ? parseInt(weeks, 10) : 1,
      });
      setDone(data);
      toast.success(data.bookings.length > 1
        ? `Serie creata: ${data.bookings.length} settimane prenotate!`
        : "Richiesta inviata! Il coach confermerà a breve.");
    } catch (err) {
      toast.error(formatApiError(err));
      if (err.response?.status === 409) {
        const r = await api.get("/bookings/slots", { params: { date } });
        setTaken(r.data.taken); setSlot("");
      }
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    const multi = done.bookings.length > 1;
    return (
      <section id="prenota" data-testid="booking-section" className="py-16 sm:py-24 lg:py-32">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 text-center">
          <CheckCircle2 className="w-16 h-16 text-emerald-400 mx-auto" />
          <h2 className="mt-6 text-2xl sm:text-3xl font-bold text-white font-heading">
            {multi ? "Serie settimanale creata!" : "Richiesta inviata!"}
          </h2>
          <div data-testid="booking-success-message" className="mt-4 text-slate-300">
            <p>
              Grazie <strong className="text-white">{done.bookings[0].name}</strong>, {multi
                ? <>la tua serie di <strong className="text-sky-400">{done.bookings.length} sessioni</strong> alle{" "}
                   <strong className="text-sky-400">{done.bookings[0].time_slot}</strong> è stata registrata:</>
                : <>la tua richiesta per il <strong className="text-sky-400">{fmtDate(done.bookings[0].date)}</strong> alle{" "}
                   <strong className="text-sky-400">{done.bookings[0].time_slot}</strong> è stata registrata.</>}
            </p>
            {multi && (
              <div className="mt-4 inline-flex flex-wrap justify-center gap-2">
                {done.bookings.map((b) => (
                  <span key={b.booking_id} className="px-3 py-1 rounded-full text-xs font-semibold bg-sky-500/10 border border-sky-500/30 text-sky-300">
                    {fmtDate(b.date)}
                  </span>
                ))}
              </div>
            )}
            {done.skipped?.length > 0 && (
              <p data-testid="booking-skipped-warning"
                 className="mt-4 text-sm text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded-xl p-3 inline-flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 shrink-0" />
                Settimane saltate (slot occupato o chiusura): {done.skipped.map(fmtDate).join(", ")}
              </p>
            )}
            <p className="mt-4">Riceverai una email di conferma appena il coach approverà {multi ? "la serie" : "l'appuntamento"}.</p>
          </div>
          <Button data-testid="booking-new-btn"
            onClick={() => { setDone(null); setForm({ name: "", email: "", phone: "", sport: "", notes: "" }); setRecurring(false); }}
            variant="outline" className="mt-8 border-sky-500/40 text-sky-400 hover:bg-sky-500/10 bg-transparent">
            Prenota un altro appuntamento
          </Button>
        </div>
      </section>
    );
  }

  return (
    <section id="prenota" data-testid="booking-section" className="py-16 sm:py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="max-w-2xl">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-orange-400">Prenota</span>
          <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white">
            Scegli giorno e orario della tua sessione
          </h2>
          <p className="mt-4 text-slate-400">Seleziona una data, poi una fascia oraria disponibile. Il coach confermerà la tua prenotazione dalla sua area riservata.</p>
        </motion.div>

        <form onSubmit={submit} className="mt-12 grid lg:grid-cols-2 gap-8">
          <div className="bg-[#10141D] border border-[#222A3B] rounded-2xl p-6 sm:p-8">
            <Label htmlFor="booking-date" className="text-slate-300 flex items-center gap-2 mb-3">
              <CalendarCheck className="w-4 h-4 text-sky-400" /> Data della sessione
            </Label>
            <Input id="booking-date" data-testid="booking-date-picker" type="date" required
              min={todayPlus(7)} value={date} onChange={(e) => setDate(e.target.value)}
              className="bg-[#08090C] border-[#222A3B] text-white [color-scheme:dark] focus:border-sky-500" />
            <Label className="text-slate-300 flex items-center gap-2 mt-8 mb-3">
              <Clock className="w-4 h-4 text-sky-400" /> Fascia oraria
            </Label>
            {closed ? (
              <p data-testid="booking-closed-message"
                 className="text-sm text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
                Il coach non è disponibile in questa data. Scegli un altro giorno.
              </p>
            ) : (
              <div className="grid grid-cols-3 gap-3">
                {slots.map((s) => {
                  const isTaken = taken.includes(s);
                  const active = slot === s;
                  return (
                    <button key={s} type="button" disabled={isTaken}
                      data-testid={`booking-time-slot-${s.replace(":", "")}`}
                      onClick={() => setSlot(s)}
                      className={`py-2.5 rounded-xl text-sm font-semibold border transition-all duration-200 ${
                        isTaken ? "bg-white/5 border-white/5 text-slate-600 line-through cursor-not-allowed"
                        : active ? "bg-sky-400 text-[#050B14] border-sky-400 shadow-[0_0_20px_rgba(56,189,248,0.35)]"
                        : "bg-[#08090C] border-[#222A3B] text-slate-300 hover:border-sky-500/50 hover:text-sky-300"}`}>
                      {s}
                    </button>
                  );
                })}
              </div>
            )}
            <p className="mt-4 text-xs text-slate-500">Le fasce barrate sono già occupate. Sessioni da 60 minuti. Prenotazioni possibili con almeno 7 giorni di preavviso.</p>

            <div className="mt-6 bg-[#08090C] border border-[#222A3B] rounded-xl p-4">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-sm font-medium text-white flex items-center gap-2">
                    <Repeat className="w-4 h-4 text-sky-400" /> Sessione ricorrente
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    Stessa ora, ogni settimana. Le settimane già occupate o chiuse vengono saltate automaticamente.
                  </p>
                </div>
                <Switch data-testid="booking-recurring-switch" checked={recurring} onCheckedChange={setRecurring} />
              </div>
              {recurring && (
                <div className="mt-4">
                  <Label className="text-slate-300">Per quante settimane?</Label>
                  <Select value={weeks} onValueChange={setWeeks}>
                    <SelectTrigger data-testid="booking-weeks-select" className="mt-2 bg-[#10141D] border-[#222A3B] text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#181E2B] border-[#222A3B] text-white">
                      {WEEKS_OPTIONS.map((w) => (
                        <SelectItem key={w} value={w} data-testid={`booking-weeks-option-${w}`}>{w} settimane</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </div>

          <div className="bg-[#10141D] border border-[#222A3B] rounded-2xl p-6 sm:p-8 space-y-5">
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <Label htmlFor="booking-name" className="text-slate-300">Nome e cognome *</Label>
                <Input id="booking-name" data-testid="booking-name-input" required value={form.name} onChange={set("name")}
                  placeholder="Mario Rossi" className="mt-2 bg-[#08090C] border-[#222A3B] text-white focus:border-sky-500" />
              </div>
              <div>
                <Label htmlFor="booking-phone" className="text-slate-300">Telefono *</Label>
                <Input id="booking-phone" data-testid="booking-phone-input" required value={form.phone} onChange={set("phone")}
                  placeholder="+39 333 1234567" className="mt-2 bg-[#08090C] border-[#222A3B] text-white focus:border-sky-500" />
              </div>
            </div>
            <div>
              <Label htmlFor="booking-email" className="text-slate-300">Email *</Label>
              <Input id="booking-email" data-testid="booking-email-input" type="email" required value={form.email} onChange={set("email")}
                placeholder="mario.rossi@email.it" className="mt-2 bg-[#08090C] border-[#222A3B] text-white focus:border-sky-500" />
            </div>
            <div>
              <Label className="text-slate-300">Sport praticato *</Label>
              <Select required value={form.sport} onValueChange={set("sport")}>
                <SelectTrigger data-testid="booking-sport-select" className="mt-2 bg-[#08090C] border-[#222A3B] text-white">
                  <SelectValue placeholder="Seleziona lo sport" />
                </SelectTrigger>
                <SelectContent className="bg-[#181E2B] border-[#222A3B] text-white">
                  {SPORTS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="booking-notes" className="text-slate-300">Note (obiettivi, esigenze...)</Label>
              <Textarea id="booking-notes" data-testid="booking-notes-input" value={form.notes} onChange={set("notes")}
                placeholder="Raccontami in due righe cosa vorresti migliorare..."
                className="mt-2 bg-[#08090C] border-[#222A3B] text-white focus:border-sky-500 min-h-[90px]" />
            </div>
            <Button type="submit" data-testid="booking-submit-button" disabled={loading}
              className="w-full bg-sky-400 text-[#050B14] hover:bg-sky-300 font-semibold h-12 text-base shadow-[0_0_25px_rgba(56,189,248,0.25)]">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : recurring ? `Prenota ${weeks} settimane con un click` : "Invia richiesta di prenotazione"}
            </Button>
            <p className="text-xs text-slate-500 text-center">
              Inviando il modulo accetti la <a href="/privacy" className="text-sky-400 hover:underline">Privacy Policy</a>.
            </p>
          </div>
        </form>
      </div>
    </section>
  );
};
